import { Edit3, Plus, Save, Trash2, Undo2 } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { cn } from "@/lib/utils";
import {
  getDetectionMatchText,
  getOptionMatchText,
  normalizeTagMatchText,
  type DocumentTagOption,
  type TagDetectionClassification,
} from "@/viewport-tools/tags";

interface PanelDetection {
  rawText: string;
  normalizedText?: string;
  classification: TagDetectionClassification;
  archivedAt?: number;
}

interface NormalizedRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

const NO_TAG_OPTION_VALUE = "__no_tag_option__";

const CLASSIFICATION_OPTIONS: Array<{ value: TagDetectionClassification; label: string }> = [
  { value: "finish_tag", label: "Finish" },
  { value: "room_tag", label: "Room" },
  { value: "door_window_tag", label: "Door / Window" },
  { value: "reference_noise", label: "Reference" },
  { value: "unknown", label: "Unknown" },
];

export interface DocumentTagEditorPanelProps<TDetection extends PanelDetection> {
  panelClassName?: string;
  shouldRenderPanel: boolean;
  showEditToggle: boolean;
  editMode: boolean;
  allowCreate: boolean;
  createMode: boolean;
  pendingId: string | null;
  createDraftRect: NormalizedRect | null;
  selectedDetection: TDetection | null;
  selectedDetectionCount: number;
  selectedActiveDetectionCount: number;
  selectedArchivedDetectionCount: number;
  selectedDeletableDetectionCount: number;
  selectedDetectionWasCreatedByUser: boolean;
  canSaveSelection: boolean;
  draftText: string;
  draftClassification: TagDetectionClassification;
  tagOptions: DocumentTagOption[];
  draftTagOptionId: string | null;
  bulkTargetOptionId: string | null;
  showBulkRetagControls: boolean;
  onDraftTextChange: (value: string) => void;
  onDraftClassificationChange: (value: TagDetectionClassification) => void;
  onDraftTagOptionChange: (value: string) => void;
  onBulkTargetOptionChange: (value: string) => void;
  onToggleBulkRetagControls: () => void;
  onRequestBulkRetag: () => void;
  onToggleEditMode: () => void;
  onToggleCreateMode: () => void;
  onSave: () => void;
  onHideSelected: () => void;
  onUnhideSelected: () => void;
  onDelete: () => void;
  onClearSelection: () => void;
}

interface TagActionButtonsProps {
  hasSelection: boolean;
  hasTagOptions: boolean;
  isMultiSelection: boolean;
  createDraftRect: NormalizedRect | null;
  pendingId: string | null;
  canSaveSelection: boolean;
  selectedActiveDetectionCount: number;
  selectedArchivedDetectionCount: number;
  selectedDeletableDetectionCount: number;
  canDeleteSelection: boolean;
  selectedDetectionWasCreatedByUser: boolean;
  onSave: () => void;
  onHideSelected: () => void;
  onUnhideSelected: () => void;
  onDelete: () => void;
  onToggleBulkRetagControls: () => void;
  onClearSelection: () => void;
}

function TagActionButtons({
  hasSelection,
  hasTagOptions,
  isMultiSelection,
  createDraftRect,
  pendingId,
  canSaveSelection,
  selectedActiveDetectionCount,
  selectedArchivedDetectionCount,
  selectedDeletableDetectionCount,
  canDeleteSelection,
  selectedDetectionWasCreatedByUser,
  onSave,
  onHideSelected,
  onUnhideSelected,
  onDelete,
  onToggleBulkRetagControls,
  onClearSelection,
}: TagActionButtonsProps) {
  const saveLabel = createDraftRect ? "Create" : isMultiSelection ? "Apply Tag" : "Save";
  const shouldShowDelete = isMultiSelection ? canDeleteSelection : selectedDetectionWasCreatedByUser;

  return (
    <div className="flex flex-wrap items-center gap-1.5">
      <Button
        type="button"
        size="sm"
        className="h-8 px-2 text-xs"
        onClick={onSave}
        disabled={pendingId !== null || !canSaveSelection}
      >
        <Save className="size-3.5" />
        {saveLabel}
      </Button>
      {hasSelection && selectedActiveDetectionCount > 0 ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 px-2 text-xs"
          onClick={onHideSelected}
          disabled={pendingId !== null}
        >
          <Trash2 className="size-3.5" />
          Hide{isMultiSelection ? ` (${selectedActiveDetectionCount})` : ""}
        </Button>
      ) : null}
      {hasSelection && selectedArchivedDetectionCount > 0 ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 px-2 text-xs"
          onClick={onUnhideSelected}
          disabled={pendingId !== null}
        >
          <Undo2 className="size-3.5" />
          Unhide{isMultiSelection ? ` (${selectedArchivedDetectionCount})` : ""}
        </Button>
      ) : null}
      {hasSelection && shouldShowDelete ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 px-2 text-xs"
          onClick={onDelete}
          disabled={pendingId !== null}
        >
          <Trash2 className="size-3.5" />
          Delete{isMultiSelection ? ` (${selectedDeletableDetectionCount})` : ""}
        </Button>
      ) : null}
      {hasSelection && hasTagOptions && !isMultiSelection ? (
        <Button
          type="button"
          size="sm"
          variant="outline"
          className="h-8 px-2 text-xs"
          onClick={onToggleBulkRetagControls}
        >
          Change all
        </Button>
      ) : null}
      <Button type="button" size="sm" variant="ghost" className="h-8 px-2 text-xs" onClick={onClearSelection}>
        Cancel
      </Button>
    </div>
  );
}

export function DocumentTagEditorPanel<TDetection extends PanelDetection>({
  panelClassName,
  shouldRenderPanel,
  showEditToggle,
  editMode,
  allowCreate,
  createMode,
  pendingId,
  createDraftRect,
  selectedDetection,
  selectedDetectionCount,
  selectedActiveDetectionCount,
  selectedArchivedDetectionCount,
  selectedDeletableDetectionCount,
  selectedDetectionWasCreatedByUser,
  canSaveSelection,
  draftText,
  draftClassification,
  tagOptions,
  draftTagOptionId,
  bulkTargetOptionId,
  showBulkRetagControls,
  onDraftTextChange,
  onDraftClassificationChange,
  onDraftTagOptionChange,
  onBulkTargetOptionChange,
  onToggleBulkRetagControls,
  onRequestBulkRetag,
  onToggleEditMode,
  onToggleCreateMode,
  onSave,
  onHideSelected,
  onUnhideSelected,
  onDelete,
  onClearSelection,
}: DocumentTagEditorPanelProps<TDetection>) {
  const hasTagOptions = tagOptions.length > 0;
  const isMultiSelection = selectedDetectionCount > 1;
  const canDeleteSelection = selectedDetectionCount > 0 && selectedDeletableDetectionCount === selectedDetectionCount;
  const selectedMatchText = selectedDetection
    ? normalizeTagMatchText(getDetectionMatchText(selectedDetection))
    : "";
  const bulkTargetOptions = selectedDetection
    ? tagOptions.filter((option) => getOptionMatchText(option) !== selectedMatchText)
    : [];

  return (
    <div
      data-document-tag-editor-ui
      className={cn(
        "absolute right-4 top-24 z-40 flex w-64 flex-col gap-2 rounded-lg border bg-background/95 p-3 shadow-lg backdrop-blur",
        panelClassName,
        !shouldRenderPanel && "hidden"
      )}
    >
      <div className="flex items-center justify-between gap-2">
        {showEditToggle ? (
          <Button
            type="button"
            variant={editMode ? "secondary" : "outline"}
            size="sm"
            className="h-8 px-2 text-xs"
            onClick={onToggleEditMode}
          >
            <Edit3 className="size-3.5" />
            {editMode ? "Editing" : "Edit Tags"}
          </Button>
        ) : (
          <div className="text-[10px] font-semibold uppercase tracking-[0.16em] text-muted-foreground">
            {isMultiSelection ? `${selectedDetectionCount} Tags` : "Tag"}
          </div>
        )}
        <Button
          type="button"
          variant={createMode ? "secondary" : "outline"}
          size="sm"
          className="h-8 px-2 text-xs"
          disabled={!editMode}
          hidden={!allowCreate}
          onClick={onToggleCreateMode}
        >
          <Plus className="size-3.5" />
          Add Tag
        </Button>
      </div>

      {createDraftRect || selectedDetection ? (
        <>
          {isMultiSelection ? (
            <div className="rounded-md border border-border/70 bg-muted/30 px-2 py-1.5 text-xs text-muted-foreground">
              {selectedDetectionCount} tags selected. Modifier-click tags to add or remove them.
            </div>
          ) : null}
          <div className="space-y-1.5">
            {hasTagOptions ? (
              <Select
                value={draftTagOptionId ?? NO_TAG_OPTION_VALUE}
                onValueChange={(value) => {
                  if (value !== NO_TAG_OPTION_VALUE) {
                    onDraftTagOptionChange(value);
                  }
                }}
              >
                <SelectTrigger className="h-9 text-sm">
                  <SelectValue placeholder="Select tag" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value={NO_TAG_OPTION_VALUE} disabled>
                    Select tag
                  </SelectItem>
                  {tagOptions.map((option) => (
                    <SelectItem key={option.id} value={option.id}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            ) : (
              <>
                <Input
                  value={draftText}
                  onChange={(event) => onDraftTextChange(event.currentTarget.value)}
                  placeholder="Tag text"
                  className="h-9 text-sm"
                />
                <Select
                  value={draftClassification}
                  onValueChange={(value) => onDraftClassificationChange(value as TagDetectionClassification)}
                >
                  <SelectTrigger className="h-9 text-sm">
                    <SelectValue placeholder="Classification" />
                  </SelectTrigger>
                  <SelectContent>
                    {CLASSIFICATION_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </>
            )}
          </div>

          <TagActionButtons
            hasSelection={selectedDetection !== null}
            hasTagOptions={hasTagOptions}
            isMultiSelection={isMultiSelection}
            createDraftRect={createDraftRect}
            pendingId={pendingId}
            canSaveSelection={canSaveSelection}
            selectedActiveDetectionCount={selectedActiveDetectionCount}
            selectedArchivedDetectionCount={selectedArchivedDetectionCount}
            selectedDeletableDetectionCount={selectedDeletableDetectionCount}
            canDeleteSelection={canDeleteSelection}
            selectedDetectionWasCreatedByUser={selectedDetectionWasCreatedByUser}
            onSave={onSave}
            onHideSelected={onHideSelected}
            onUnhideSelected={onUnhideSelected}
            onDelete={onDelete}
            onToggleBulkRetagControls={onToggleBulkRetagControls}
            onClearSelection={onClearSelection}
          />

          {!selectedDetection ? (
            <div className="text-xs text-muted-foreground">
              Draw a box on the document, then choose the tag details and create it.
            </div>
          ) : null}
          {selectedDetection && hasTagOptions && !isMultiSelection ? (
            <div className="space-y-1.5">
              {showBulkRetagControls ? (
                <div className="space-y-1.5 rounded-md border border-border/70 p-2">
                  <div className="text-[10px] font-semibold uppercase tracking-[0.12em] text-muted-foreground">
                    Change matching
                  </div>
                  <Select
                    value={bulkTargetOptionId ?? NO_TAG_OPTION_VALUE}
                    onValueChange={(value) => {
                      if (value !== NO_TAG_OPTION_VALUE) {
                        onBulkTargetOptionChange(value);
                      }
                    }}
                  >
                    <SelectTrigger className="h-8 text-xs">
                      <SelectValue placeholder="Change to tag" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value={NO_TAG_OPTION_VALUE} disabled>
                        Change to tag
                      </SelectItem>
                      {bulkTargetOptions.map((option) => (
                        <SelectItem key={option.id} value={option.id}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-8 px-2 text-xs"
                    disabled={!bulkTargetOptionId || pendingId !== null || bulkTargetOptions.length === 0}
                    onClick={onRequestBulkRetag}
                  >
                    Apply to all
                  </Button>
                </div>
              ) : null}
            </div>
          ) : null}
        </>
      ) : (
        <div className="text-sm text-muted-foreground">
          {editMode
            ? "Select a tag to edit it, change all matching tags, or use Add Tag to draw a new one."
            : showEditToggle
              ? "Enable Edit Tags to add, duplicate, move, hide, or unhide tags."
              : "Use the Tags tool in the left rail to add, duplicate, move, hide, unhide, delete, or bulk-change tags."}
        </div>
      )}
    </div>
  );
}
